import type { IAccount } from "@/modules/account/common/types";
import { TransferKeys } from "@/modules/transaction/common/enum";
import { AppPaths } from "@/shared/common/enums";
import type { ICommission, IObject } from "@/shared/common/types";
import { BusinessLayout, Commission, } from "@/shared/components";
import { useNotify } from "@/shared/hooks";
import { debounce } from "@/shared/utils";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface Props<T> {
  title?: string,
  getCommission?: (p: IObject) => Promise<{ data?: ICommission }>,
  children: ReactElement,
  saveTemplate: (values: T) => Promise<string>,
  canGetCommission?: boolean,
  fieldChanges?: IObject;
  page: string;
  isEdit?: boolean;
  isGetTemplateLoading?: boolean;
  accountsList: IAccount[];
  withCommission?: boolean;
  hasChanges?: boolean;
  className?: string;
  isLoading?: boolean;
}

export const TemplateLayout = <_, T>(
  {
    children,
    title,
    isGetTemplateLoading,
    saveTemplate,
    page,
    isLoading,
    accountsList,
    withCommission = true,
    isEdit = false,
    hasChanges = false,
    className,
    getCommission,
    fieldChanges,
    canGetCommission,
  }: Props<T>,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    showErrorAlertNotification,
    showWarningAlertNotification,
    showSuccessSnackbarNotification,
  } = useNotify();
  const { setValue, setError, watch, isSubmitting, trigger } = useFormProps();
  const [isSaved, setIsSaved] = useState<boolean>(false);

  const values = watch?.() || {};

  const handleSaveTemplate = async (isSave?: boolean) => {
    trigger?.().then((isValid) => {
      if (isValid) {
        saveTemplate(values as T)
          .then(() => {
            setIsSaved(true);
            const notificationKey = isEdit ?
              "alerts.templateHasBeenSuccessfullyUpdated" :
              "alerts.templateHasBeenSuccessfullySaved";
            showSuccessSnackbarNotification(t(notificationKey));
            setTimeout(() => navigate(AppPaths.Templates));
          })
          .catch((error) => {
            const errors = error?.data?.errors;
            const warning = error?.data?.warnings?.[0];

            if (warning) {
              showWarningAlertNotification(t(warning));
              if (isSave) {
                document.getElementById("form-container")?.scrollTo({
                  top: 0, behavior: "smooth"
                });
              }
            } else if (errors) {
              Object.keys(errors).forEach((key) => {
                setError?.(key, { message: errors[key] });
              });
            } else {
              showErrorAlertNotification(error);
              document.getElementById("form-container")?.scrollTo({ top: 0, behavior: "smooth" });
            }
            return Promise.reject(error);
          });
      }
    });
  };

  const handleGetCommission = useMemo(() =>
    debounce((fieldChanges: IObject) => {
      getCommission?.(fieldChanges).then(({ data }) => {
        setValue(TransferKeys.CommissionData, data || 0);
      });
    }, 700), []);

  useEffect(() => {
    if (canGetCommission && withCommission) {
      handleGetCommission(fieldChanges as IObject);
    }
  }, [fieldChanges]);

  return (
    <BusinessLayout
      title={title}
      isEdit={isEdit}
      isSaved={isSaved}
      className={className}
      hasChanges={hasChanges}
    >
      <>
        {isGetTemplateLoading ? (
          <div className="flexbox align-items--center" style={{ minHeight: "50vh" }}>
            <Spinner isFullScreen={false}/>
          </div>
        ) : children}
        {withCommission && (
          <div className="commission-container-wrapper mt-16">
            <Commission accountsList={accountsList}/>
          </div>
        )}
        <div
          className="full-width flexbox flex-direction--row justify-content--end template-form-actions">
          <Button
            className="mt-32 truncate-btn full-width"
            buttonActionType="button"
            buttonText={t("general.save")}
            type="primary"
            data-testid="save-template"
            data-ga-id="save-template"
            isLoading={isLoading}
            data-ga-action={`Save ${page} template`}
            disabled={isSubmitting || (isEdit && !hasChanges) || isLoading}
            onClick={() => handleSaveTemplate()}
          />
        </div>
      </>
    </BusinessLayout>
  );
};
